// components/MyPage/MyItemHistory.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Paper, Stack,
  Accordion, AccordionSummary, AccordionDetails, useMediaQuery, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory2';
import {
  Timeline, TimelineItem, TimelineSeparator,
  TimelineConnector, TimelineContent, TimelineDot,
} from '@mui/lab';
import dayjs from 'dayjs';
import api from '../../api/axios';
import SafeChip from '../Common/SafeChip';
import { tablePaperSX } from './HistoryStyles';
import { useTheme } from '@mui/material/styles';

const PAGE_SIZE = 5;

const MyItemHistory = () => {
  const [logs, setLogs] = useState(null);
  const [page, setPage] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    Promise.all([
      api.get('/api/rewards/items/my/used-items/'),
      api.get('/api/rewards/items/my/refund-records/'),
    ]).then(([u, r]) => {
      const used = (u.data.logs || []).map((l, idx) => ({
        id: l.id ? `u-${l.id}` : `u-${idx}-${l.item_name}`,
        type: 'used',
        name: l.item_name,
        date: l.used_at,
        status: l.confirmed_type
      }));
      const refund = (r.data.logs || []).map((l, idx) => ({
        id: l.id ? `r-${l.id}` : `r-${idx}-${l.item_name}`,
        type: 'refund',
        name: l.item_name,
        date: l.refunded_at,
        amount: l.amount
      }));
      setLogs([...used, ...refund].sort((a, b) => new Date(b.date) - new Date(a.date)));
    }).catch(() => setLogs([]));
  }, []);

  if (logs === null) return <CircularProgress />;
  if (!logs.length) return <Typography>내역이 없습니다.</Typography>;

  const dotColor = log =>
    log.type === 'refund' ? 'info.main'
      : log.status === 'approve' ? 'success.main'
        : log.status === 'pending' ? 'warning.main'
          : 'grey.500';

  const pageCount = Math.ceil(logs.length / PAGE_SIZE);
  const slicedLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        📜 쿠폰 / 아이템 내역
      </Typography>

      {isMobile ? (
        <>
          <Stack spacing={2}>
            {slicedLogs.map(log => (
              <Accordion key={log.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack spacing={0.5}>
                    <Typography fontWeight={600} fontSize={13}>
                      {dayjs(log.date).format('YYYY.MM.DD')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InventoryIcon fontSize="small" />
                      <Typography fontWeight={600}>
                        {log.name}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: '#fafafa',
                    borderLeft: `4px solid ${dotColor(log)}`,
                    px: 2, py: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SafeChip
                      label={log.type === 'used' ? '사용' : '환불'}
                      color={log.type === 'used' ? 'primary' : 'info'}
                      size="small"
                    />
                    <Typography fontWeight={600}>{log.name}</Typography>
                    <Box flexGrow={1} />
                    {log.type === 'used' ? (
                      <SafeChip
                        label={
                          log.status === 'approve' ? '승인'
                            : log.status === 'refund' ? '환불됨'
                              : '대기중'}
                        color={
                          log.status === 'approve' ? 'success'
                            : log.status === 'refund' ? 'info'
                              : 'warning'}
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="primary.main" fontWeight={600}>
                        +{log.amount} Leaf🌿
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    {dayjs(log.date).format('YYYY-MM-DD HH:mm')}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>

          {/* 페이지네이션 */}
          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Timeline position="alternate">
          {logs.map(log => {
            const pos = log.type === 'refund' ? 'left' : 'right';
            const align = pos === 'left' ? 'flex-start' : 'flex-end';

            return (
              <TimelineItem key={log.id}>
                <TimelineSeparator>
                  <TimelineDot sx={{ bgcolor: dotColor(log) }} />
                  <TimelineConnector />
                </TimelineSeparator>

                <TimelineContent sx={{ pb: 2 }}>
                  <Paper
                    sx={{
                      ...tablePaperSX,
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ alignSelf: align }}
                    >
                      {dayjs(log.date).format('YYYY-MM-DD HH:mm')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SafeChip
                        label={log.type === 'used' ? '사용' : '환불'}
                        color={log.type === 'used' ? 'primary' : 'info'}
                        size="small"
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {log.name}
                      </Typography>
                      <Box flexGrow={1} />
                      {log.type === 'used'
                        ? <SafeChip
                          label={
                            log.status === 'approve' ? '승인'
                              : log.status === 'refund' ? '환불됨'
                                : '대기중'
                          }
                          color={
                            log.status === 'approve' ? 'success'
                              : log.status === 'refund' ? 'info'
                                : 'warning'
                          }
                          size="small"
                        />
                        : <Typography
                          variant="body2"
                          color="primary.main"
                          fontWeight={600}
                        >
                          +{log.amount} Leaf🌿
                        </Typography>}
                    </Box>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      )}
    </Box>
  );
};

export default MyItemHistory;